import type { StudioDraft } from './studio-draft';
import { presentationLayoutLabel } from '../../../renderer/src/social-presentation';
import { describeRecoveryValue, type RecoveryIssue } from './draft-recovery-description';

/** Explain the proposed change without exposing serialized settings. */
export function recoveryNotice(issues: readonly RecoveryIssue[], draft: StudioDraft) {
  const changes = issues.map(issue => {
    const [field, identity] = issue.field.split('.');
    const layout = identity ? draft.appearances[identity] : undefined;
    if (field === 'appearances') return { label: issue.label,
      summary: layout ? `Continue with the ${presentationLayoutLabel(layout.presentation)} layout.` : 'Use the default layouts for your video formats.',
      action: layout ? `Use ${presentationLayoutLabel(layout.presentation)} layout` : 'Use default layouts' };
    if (field === 'exportRange') return { label: 'Selected footage', summary: 'The saved IN/OUT points are outside this film. Select the full film to continue.', action: 'Use full film' };
    if (field === 'cinematicSequence') return { label: 'Camera keyframes', summary: issue.removed || 'Some camera keyframes could not be opened and will be removed.', action: 'Use these changes' };
    if (field === 'composition') return { label: 'Saved settings', summary: 'The saved settings could not be opened. Start with the default video settings.', action: 'Use default settings' };
    const value = field && field in draft ? draft[field as keyof StudioDraft] : undefined;
    if (value !== undefined && (value === null || typeof value !== 'object')) {
      const replacement = describeRecoveryValue(field!, value);
      return { label: issue.label, summary: `Use ${replacement} for ${issue.label.toLowerCase()}.`, action: field === 'distance' ? `Use ${replacement} lens` : 'Use these changes' };
    }
    return { label: issue.label, summary: `Use the current ${issue.label.toLowerCase()} settings.`, action: 'Use these changes' };
  });
  const first = issues[0]?.field;
  const title = issues.length !== 1 ? `${issues.length} saved settings need attention`
    : first?.startsWith('appearances') ? 'Your saved layout couldn’t be opened'
    : first === 'exportRange' ? 'Your selected footage needs attention'
    : first === 'cinematicSequence' ? 'Some camera keyframes couldn’t be opened'
    : 'A saved setting couldn’t be opened';
  return { title, changes, action: changes.length === 1 ? changes[0]!.action : 'Use these changes' };
}
